import React, { useState, useEffect, useCallback, useRef, createContext, useContext, type ReactNode } from 'react';
import { apiUrl } from '@/lib/api-url';

interface SubscriptionStatus {
  isSubscriber: boolean;
  status: string | null;
  creditsRemaining: number;
  creditsUsed: number;
  currentPeriodEnd: string | null;
  unlockedSeedIds: number[];
  checkoutUrl: string;
}

interface SubscriptionContextType {
  isSubscriber: boolean;
  status: string | null;
  creditsRemaining: number;
  creditsUsed: number;
  currentPeriodEnd: string | null;
  unlockedSeedIds: number[];
  checkoutUrl: string;
  fanbasisUrl: string;
  loading: boolean;
  unlockPhotos: (seedUserId: number) => Promise<{ ok: boolean; data: any }>;
  refresh: () => Promise<void>;
  startCheckoutPolling: () => void;
  stopCheckoutPolling: () => void;
}

const SubscriptionContext = createContext<SubscriptionContextType | null>(null);

const POLL_INTERVAL = 5000;
const POLL_TIMEOUT = 600000;
const VISIBILITY_DEBOUNCE = 2000;
const MAX_CONSECUTIVE_ERRORS = 5;
const ERROR_BACKOFF = [10000, 30000, 60000, 60000, 60000];

export function SubscriptionProvider({
  children,
  token,
  onVipConfirmed,
}: {
  children: ReactNode;
  token: string | null;
  onVipConfirmed?: () => void;
}) {
  const [data, setData] = useState<SubscriptionStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const pollingRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const pollingStartRef = useRef<number>(0);
  const visibilityDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const consecutiveErrorsRef = useRef(0);
  const backoffTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const onVipConfirmedRef = useRef(onVipConfirmed);
  onVipConfirmedRef.current = onVipConfirmed;

  const handleFetchError = useCallback(() => {
    consecutiveErrorsRef.current++;
    const errCount = consecutiveErrorsRef.current;
    if (errCount >= MAX_CONSECUTIVE_ERRORS && pollingRef.current) {
      clearInterval(pollingRef.current);
      pollingRef.current = null;
    }
    if (errCount <= 2) {
      console.warn(`[Subscription] Status fetch failed (attempt ${errCount})`);
    }
  }, []);

  const fetchStatus = useCallback(async () => {
    const currentToken = token || localStorage.getItem('papacito_token');
    if (!currentToken) {
      setLoading(false);
      return;
    }
    try {
      const res = await fetch(apiUrl('/subscription/status'), {
        headers: {
          Authorization: `Bearer ${currentToken}`,
          'Content-Type': 'application/json',
        },
      });
      if (res.ok) {
        const newData: SubscriptionStatus = await res.json();
        if ((newData as any).error === 'temporarily_unavailable') {
          handleFetchError();
          return;
        }
        consecutiveErrorsRef.current = 0;
        setData(prev => {
          if ((!prev || !prev.isSubscriber) && newData.isSubscriber) {
            onVipConfirmedRef.current?.();
            (async () => {
              try {
                const meetupRes = await fetch(apiUrl('/api/likes/meetup-request'), {
                  headers: { Authorization: `Bearer ${currentToken}` },
                });
                if (meetupRes.ok) {
                  const meetupData = await meetupRes.json();
                  if (meetupData?.pendingMeetupRequest) {
                    window.dispatchEvent(
                      new CustomEvent('papacito:vip_meetup_unlocked', {
                        detail: meetupData.pendingMeetupRequest,
                      })
                    );
                  }
                }
              } catch (err) {
                console.warn('[Subscription] Meetup unlock check failed:', err);
              }
            })();
          }
          return newData;
        });

        if (newData.isSubscriber && pollingRef.current) {
          clearInterval(pollingRef.current);
          pollingRef.current = null;
        }
      } else {
        handleFetchError();
      }
    } catch {
      handleFetchError();
    } finally {
      setLoading(false);
    }
  }, [token, handleFetchError]);

  useEffect(() => {
    if (token) {
      fetchStatus();
    } else {
      setData(null);
      setLoading(false);
    }
  }, [token, fetchStatus]);

  useEffect(() => {
    if (!token) return;

    const handleVisibility = () => {
      if (document.visibilityState === 'visible') {
        if (visibilityDebounceRef.current) clearTimeout(visibilityDebounceRef.current);
        visibilityDebounceRef.current = setTimeout(() => {
          fetchStatus();
        }, VISIBILITY_DEBOUNCE);
      }
    };

    const handleFocus = () => {
      if (visibilityDebounceRef.current) clearTimeout(visibilityDebounceRef.current);
      visibilityDebounceRef.current = setTimeout(() => {
        fetchStatus();
      }, VISIBILITY_DEBOUNCE);
    };

    document.addEventListener('visibilitychange', handleVisibility);
    window.addEventListener('focus', handleFocus);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibility);
      window.removeEventListener('focus', handleFocus);
      if (visibilityDebounceRef.current) clearTimeout(visibilityDebounceRef.current);
    };
  }, [token, fetchStatus]);

  const startCheckoutPolling = useCallback(() => {
    if (pollingRef.current) clearInterval(pollingRef.current);
    pollingStartRef.current = Date.now();

    pollingRef.current = setInterval(() => {
      if (Date.now() - pollingStartRef.current > POLL_TIMEOUT) {
        if (pollingRef.current) clearInterval(pollingRef.current);
        pollingRef.current = null;
        return;
      }
      fetchStatus();
    }, POLL_INTERVAL);
  }, [fetchStatus]);

  const stopCheckoutPolling = useCallback(() => {
    if (pollingRef.current) {
      clearInterval(pollingRef.current);
      pollingRef.current = null;
    }
  }, []);

  useEffect(() => {
    return () => {
      if (pollingRef.current) clearInterval(pollingRef.current);
      if (visibilityDebounceRef.current) clearTimeout(visibilityDebounceRef.current);
      if (backoffTimerRef.current) clearTimeout(backoffTimerRef.current);
    };
  }, []);

  const unlockPhotos = useCallback(
    async (seedUserId: number) => {
      const currentToken = token || localStorage.getItem('papacito_token');
      const res = await fetch(apiUrl(`/subscription/unlock/${seedUserId}`), {
        method: 'POST',
        headers: currentToken ? { Authorization: `Bearer ${currentToken}` } : {},
      });
      const result = await res.json();
      if (res.ok) {
        await fetchStatus();
      }
      return { ok: res.ok, data: result };
    },
    [token, fetchStatus]
  );

  const value: SubscriptionContextType = {
    isSubscriber: data?.isSubscriber || false,
    status: data?.status ?? null,
    creditsRemaining: data?.creditsRemaining ?? 0,
    creditsUsed: data?.creditsUsed ?? 0,
    currentPeriodEnd: data?.currentPeriodEnd ?? null,
    unlockedSeedIds: data?.unlockedSeedIds ?? [],
    checkoutUrl: data?.checkoutUrl ?? '',
    fanbasisUrl: data?.checkoutUrl ?? '',
    loading,
    unlockPhotos,
    refresh: fetchStatus,
    startCheckoutPolling,
    stopCheckoutPolling,
  };

  return (
    <SubscriptionContext.Provider value={value}>
      {children}
    </SubscriptionContext.Provider>
  );
}

export function useSubscription() {
  const context = useContext(SubscriptionContext);
  if (!context) {
    throw new Error('useSubscription must be used within a SubscriptionProvider');
  }
  return context;
}
